import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, CheckCircle } from 'lucide-react';
import { Resource, UIComponent } from '../../types';
import { useTranslation } from '../../contexts/TranslationContext';

interface MatchingObjectsComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface MatchingItem {
  id: string;
  type: 'objects' | 'numeral' | 'ten_frame' | 'word';
  value: number;
  content: any;
  matched: boolean;
  matchedWith?: string;
}

export const MatchingObjectsComponent: React.FC<MatchingObjectsComponentProps> = ({ 
  resource, 
  component, 
  onAction 
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<MatchingItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [matches, setMatches] = useState<Record<string, string>>({});
  const [completed, setCompleted] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Get settings from resource or component props
  const maxValue = resource.max_value || 10;
  const itemCount = resource.item_count || 4;
  const includeObjects = resource.include_objects !== false;
  const includeNumerals = resource.include_numerals !== false;
  const includeTenFrames = resource.include_ten_frames !== false;
  const includeWords = resource.include_words !== false;

  const generateItems = useCallback(() => {
    const newItems: MatchingItem[] = [];
    const usedValues = new Set<number>();

    // Generate unique values for matching
    while (usedValues.size < itemCount) {
      const value = Math.floor(Math.random() * maxValue) + 1;
      if (!usedValues.has(value)) {
        usedValues.add(value);
      }
    }

    const values = Array.from(usedValues);
    let itemId = 0;

    // Generate objects representation
    if (includeObjects) {
      values.forEach(value => {
        newItems.push({
          id: `obj_${itemId++}`,
          type: 'objects',
          value,
          content: {
            count: value,
            layout: resource.object_layout || 'random_clustered'
          },
          matched: false
        });
      });
    }

    // Generate numeral representation
    if (includeNumerals) {
      values.forEach(value => {
        newItems.push({
          id: `num_${itemId++}`,
          type: 'numeral',
          value,
          content: { number: value },
          matched: false
        });
      });
    }

    // Generate ten frame representation
    if (includeTenFrames) {
      values.forEach(value => {
        newItems.push({
          id: `tf_${itemId++}`,
          type: 'ten_frame',
          value,
          content: { 
            frame_value: value,
            rows: 2,
            cols: 5
          },
          matched: false
        });
      });
    }

    // Generate word representation
    if (includeWords) {
      const numberWords = [
        t('matchingObjects.numberWords.one'),
        t('matchingObjects.numberWords.two'),
        t('matchingObjects.numberWords.three'),
        t('matchingObjects.numberWords.four'),
        t('matchingObjects.numberWords.five'),
        t('matchingObjects.numberWords.six'),
        t('matchingObjects.numberWords.seven'),
        t('matchingObjects.numberWords.eight'),
        t('matchingObjects.numberWords.nine'),
        t('matchingObjects.numberWords.ten')
      ];
      values.forEach(value => {
        newItems.push({
          id: `word_${itemId++}`,
          type: 'word',
          value,
          content: { word: numberWords[value - 1] || value.toString() },
          matched: false
        });
      });
    }

    // Shuffle items
    const shuffledItems = newItems.sort(() => Math.random() - 0.5);
    setItems(shuffledItems);
    setMatches({});
    setCompleted(false);
    setAttempts(0);
  }, [maxValue, itemCount, includeObjects, includeNumerals, includeTenFrames, includeWords, resource.object_layout, t]);

  const handleItemClick = (itemId: string) => {
    if (items.find(item => item.id === itemId)?.matched) {
      return; // Already matched
    }

    if (!selectedItem) {
      setSelectedItem(itemId);
      onAction?.('item_selected', { itemId });
    } else {
      const firstItem = items.find(item => item.id === selectedItem);
      const secondItem = items.find(item => item.id === itemId);

      if (firstItem && secondItem && firstItem.value === secondItem.value && selectedItem !== itemId) {
        // Match found!
        const newMatches = { ...matches, [selectedItem]: itemId, [itemId]: selectedItem };
        setMatches(newMatches);
        
        const newItems = items.map(item => 
          item.id === selectedItem || item.id === itemId 
            ? { ...item, matched: true, matchedWith: item.id === selectedItem ? itemId : selectedItem }
            : item
        );
        setItems(newItems);

        onAction?.('match_found', { 
          item1: selectedItem, 
          item2: itemId, 
          value: firstItem.value 
        });

        // Check if all items are matched
        const allMatched = newItems.every(item => item.matched);
        if (allMatched) {
          setCompleted(true);
          onAction?.('matching_completed', { 
            attempts, 
            totalMatches: Object.keys(newMatches).length / 2 
          });
        }
      } else {
        // No match
        setAttempts(prev => prev + 1);
        onAction?.('match_attempted', { 
          item1: selectedItem, 
          item2: itemId, 
          correct: false 
        });
      }
      setSelectedItem(null);
    }
  };

  const renderItem = (item: MatchingItem) => {
    const isSelected = selectedItem === item.id;
    const isMatched = item.matched;

    const baseClasses = `
      p-4 rounded-lg border-2 transition-all cursor-pointer
      ${isMatched 
        ? 'bg-green-50 border-green-300 opacity-75' 
        : isSelected 
          ? 'bg-blue-50 border-blue-400 shadow-md' 
          : 'bg-white border-gray-300 hover:border-gray-400 hover:shadow-sm'
      }
    `;

    switch (item.type) {
      case 'objects':
        return (
          <div 
            key={item.id}
            className={baseClasses}
            onClick={() => handleItemClick(item.id)}
          >
            <div className="flex flex-wrap gap-1 justify-center">
              {Array.from({ length: item.content.count }, (_, i) => (
                <div 
                  key={i}
                  className="w-3 h-3 bg-blue-500 rounded-full"
                />
              ))}
            </div>
            {isMatched && <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-1" />}
          </div>
        );

      case 'numeral':
        return (
          <div 
            key={item.id}
            className={`${baseClasses} text-center`}
            onClick={() => handleItemClick(item.id)}
          >
            <div className="text-2xl font-bold text-gray-800">
              {item.content.number}
            </div>
            {isMatched && <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-1" />}
          </div>
        );

      case 'ten_frame':
        return (
          <div 
            key={item.id}
            className={baseClasses}
            onClick={() => handleItemClick(item.id)}
          >
            <div className="grid grid-cols-5 gap-1">
              {Array.from({ length: 10 }, (_, i) => (
                <div 
                  key={i}
                  className={`w-3 h-3 rounded border ${
                    i < item.content.frame_value 
                      ? 'bg-blue-500 border-blue-600' 
                      : 'bg-gray-100 border-gray-300'
                  }`}
                />
              ))}
            </div>
            {isMatched && <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-1" />}
          </div>
        );

      case 'word':
        return (
          <div 
            key={item.id}
            className={`${baseClasses} text-center`}
            onClick={() => handleItemClick(item.id)}
          >
            <div className="text-lg font-medium text-gray-700 capitalize">
              {item.content.word}
            </div>
            {isMatched && <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-1" />}
          </div>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    generateItems();
  }, [generateItems]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">{t('matchingObjects.matchTheRepresentations')}</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={generateItems}
            className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors"
            title={t('matchingObjects.generateNewItems')}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {completed && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">{t('matchingObjects.allMatchesFound')}</span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            {t('matchingObjects.attempts')}: {attempts} | {t('matchingObjects.matches')}: {Object.keys(matches).length / 2}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {items.map(renderItem)}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{t('matchingObjects.attempts')}: {attempts}</span>
        <span>{t('matchingObjects.matches')}: {Object.keys(matches).length / 2}</span>
        <span>{t('matchingObjects.total')}: {items.length / 2}</span>
      </div>

      {selectedItem && (
        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
          {t('matchingObjects.clickAnotherItem')}
        </div>
      )}
    </div>
  );
};
